import { useMemo, type ReactNode } from 'react'
import { Area, Bar, CartesianGrid, ComposedChart, LabelList, Line, XAxis, YAxis } from 'recharts'
import { Calendar, TrendingUp, type LucideIcon } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/shared/components/ui/chart'
import { colorHex } from '../../lib/reporteColor'
import { formatNumero } from '../../lib/paisMapa'
import { rangoSemana, type Granularidad } from '../../lib/serieGranularidad'

export interface FechaDatum {
  fecha: string // ISO 'YYYY-MM-DD' o periodo 'YYYY-MM'
  total: number | string
  /** Serie secundaria opcional (ej. nº de facturas), dibujada como línea gris. */
  secondary?: number | string
  /** Porcentaje opcional que se muestra en el tooltip (ej. % participación). */
  percent?: number
  /** Valor extra fijo mostrado en tooltip (ej. validados totales como contexto). */
  validados?: number
  /** Tercera serie opcional, dibujada como línea adicional. */
  tertiary?: number | string
}

interface Props {
  title: string
  data: FechaDatum[]
  colorNombre: string
  colorTono?: string
  isLoading?: boolean
  icon?: LucideIcon
  height?: number
  /** Si true, la gráfica llena la altura del card (para igualar a otra de al
   *  lado). `height` se usa como altura mínima. */
  fill?: boolean
  formatValue?: (v: number) => string
  /** Etiqueta de la serie secundaria. Si se pasa, se dibuja la línea gris. */
  secondaryLabel?: string
  /** Etiqueta de la serie principal en el tooltip. Default: 'Total'. */
  primaryLabel?: string
  secondaryFormatValue?: (v: number) => string
  /** Etiqueta de la tercera serie. Si se pasa, se dibuja como línea adicional. */
  tertiaryLabel?: string
  tertiaryFormatValue?: (v: number) => string
  /** Estadísticas compactas en el header (ej. promedios diarios). Cada una
   *  lleva un punto de color según la serie a la que refiere. Si se pasan,
   *  reemplazan a la leyenda (los puntos ya identifican cada serie). */
  stats?: Array<{ label: string; value: string; series?: 'primary' | 'secondary' }>
  /** Id de configuración para el formateo de números según el país del reporte. */
  configId?: number
  /** Si true, dibuja una barra verde semitransparente con el campo `percent` de cada dato. */
  showPercentBar?: boolean
  /** Controles opcionales (ej. toggle de granularidad) mostrados junto al título. */
  headerActions?: ReactNode
  /** Granularidad de la serie. En 'semana' el eje muestra el rango (7 sep – 13
   *  sep) y se dibujan separadores verticales entre semanas. */
  granularidad?: Granularidad
  /** Contenido extra opcional en el tooltip, bajo el encabezado de fecha.
   *  Recibe el punto de datos (con cualquier campo extra que traiga `data`). */
  tooltipExtra?: (punto: Record<string, unknown>) => ReactNode
}

function parseDateSafe(s: string): Date {
  // 'YYYY-MM' o 'YYYY-MM-DD'
  if (/^\d{4}-\d{2}$/.test(s)) return new Date(s + '-01T12:00:00')
  if (s.includes('T')) return new Date(s)
  return new Date(s + 'T12:00:00')
}

function formatTick(s: string, granularidad?: Granularidad): string {
  if (granularidad === 'semana') return rangoSemana(s)
  const d = parseDateSafe(s)
  // Si es periodo mensual, muestra "May 26", si es diario, "12 may"
  if (granularidad === 'mes' || /^\d{4}-\d{2}$/.test(s)) {
    return d.toLocaleDateString('es-EC', { month: 'short', year: '2-digit' })
  }
  return d.toLocaleDateString('es-EC', { day: '2-digit', month: 'short' })
}

export function AreaChartFecha({
  title,
  data,
  colorNombre,
  colorTono = '600',
  isLoading,
  icon: Icon = TrendingUp,
  height = 280,
  fill,
  formatValue,
  secondaryLabel,
  primaryLabel = 'Total',
  secondaryFormatValue,
  tertiaryLabel,
  tertiaryFormatValue,
  stats,
  configId,
  showPercentBar,
  headerActions,
  granularidad,
  tooltipExtra,
}: Props) {
  const esSemanal = granularidad === 'semana'
  const stroke = colorHex(colorNombre, colorTono)
  const SECONDARY_COLOR = '#9ca3af' // gris (gray-400)
  const PERCENT_COLOR = '#2fbc80'   // verde corporativo
  const TERTIARY_COLOR = '#8d82bc'  // púrpura corporativo
  const showSecondary = Boolean(secondaryLabel)
  const showTertiary = Boolean(tertiaryLabel)
  // En modo fill el card se estira (flex) y la gráfica ocupa el alto disponible,
  // con `height` como mínimo. Si no, alto fijo.
  const cardClass = fill ? 'flex flex-col h-full' : ''
  const contentClass = fill ? 'flex-1 min-h-0' : ''
  const chartStyle = fill ? { height: '100%', minHeight: height } : { height }

  const formattedData = useMemo(
    () =>
      data.map((item) => ({
        fecha: item.fecha,
        total: typeof item.total === 'string' ? parseFloat(item.total) || 0 : item.total,
        secondary:
          item.secondary === undefined || item.secondary === null
            ? 0
            : typeof item.secondary === 'string'
              ? parseFloat(item.secondary) || 0
              : item.secondary,
        tertiary:
          item.tertiary === undefined || item.tertiary === null
            ? undefined
            : typeof item.tertiary === 'string'
              ? parseFloat(item.tertiary) || 0
              : item.tertiary,
        percent: item.percent,
        validados: item.validados,
        fechaDisplay: formatTick(item.fecha, granularidad),
      })),
    [data, granularidad],
  )

  const chartConfig = {
    total: { label: primaryLabel, color: stroke },
    secondary: { label: secondaryLabel ?? 'Secundario', color: SECONDARY_COLOR },
    tertiary: { label: tertiaryLabel ?? 'Terciario', color: TERTIARY_COLOR },
    percent: { label: '% participación', color: PERCENT_COLOR },
  } satisfies ChartConfig

  if (isLoading) {
    return (
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            <Icon className="h-4 w-4 text-muted-foreground" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton style={{ height }} className="w-full" />
        </CardContent>
      </Card>
    )
  }

  if (formattedData.length === 0) {
    return (
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            <Icon className="h-4 w-4 text-muted-foreground" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div
            style={{ height }}
            className="flex flex-col items-center justify-center text-muted-foreground"
          >
            <Calendar className="h-6 w-6 mb-2 text-muted-foreground/50" />
            <p className="text-xs font-medium">Sin datos de fechas</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={`border border-gray-300 dark:border-gray-700 overflow-hidden w-full ${cardClass}`}>
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 pb-0 pt-3">
        <div className="flex items-center gap-3 min-w-0">
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            <Icon className="h-4 w-4 text-muted-foreground" />
            {title}
          </CardTitle>
          {headerActions}
        </div>
        {stats && stats.length > 0 ? (
          <div className="flex items-center gap-4 sm:gap-6">
            {stats.map((s) => (
              <div key={s.label} className="flex flex-col items-end leading-tight">
                <span className="flex items-center gap-1 text-[10px] text-muted-foreground whitespace-nowrap">
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ background: s.series === 'secondary' ? SECONDARY_COLOR : stroke }}
                  />
                  {s.label}
                </span>
                <span className="text-base font-semibold tabular-nums text-foreground">
                  {s.value}
                </span>
              </div>
            ))}
          </div>
        ) : (showSecondary || showTertiary) ? (
          <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <span className="inline-block h-2 w-2 rounded-full" style={{ background: stroke }} />
              {primaryLabel}
            </span>
            {showTertiary && (
              <span className="flex items-center gap-1">
                <span className="inline-block h-2 w-2 rounded-full" style={{ background: TERTIARY_COLOR }} />
                {tertiaryLabel}
              </span>
            )}
            {showSecondary && (
              <span className="flex items-center gap-1">
                <span className="inline-block h-2 w-2 rounded-full" style={{ background: SECONDARY_COLOR }} />
                {secondaryLabel}
              </span>
            )}
          </div>
        ) : null}
      </CardHeader>
      <CardContent className={`p-0 px-1 overflow-hidden ${contentClass}`}>
        <ChartContainer
          config={chartConfig}
          className="!aspect-auto w-full max-w-full"
          style={chartStyle}
        >
          <ComposedChart data={formattedData} margin={{ top: 42, right: 18, left: 14, bottom: 15 }}>
            <defs>
              <linearGradient id={`area-${colorNombre}-${colorTono}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={stroke} stopOpacity={0.25} />
                <stop offset="95%" stopColor={stroke} stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={esSemanal} className="stroke-muted" />
            <XAxis
              dataKey="fechaDisplay"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              interval="preserveStartEnd"
              className="text-xs"
            />
            <YAxis yAxisId="left" tickLine={false} axisLine={false} tick={false} width={10} />
            {showTertiary && (
              <YAxis
                yAxisId="tertiary"
                orientation="right"
                tickLine={false}
                axisLine={false}
                tick={false}
                width={0}
              />
            )}
            {showSecondary && (
              <YAxis
                yAxisId="right"
                orientation="right"
                tickLine={false}
                axisLine={false}
                tick={false}
                width={10}
                // Headroom ×3 para que la línea secundaria (facturas) se
                // asiente en la franja baja del gráfico, por debajo de la
                // serie principal y sus etiquetas.
                domain={[0, (max: number) => Math.max(Math.ceil(max * 3), 1)]}
              />
            )}
            {showPercentBar && (
              <YAxis
                yAxisId="percent"
                orientation="right"
                tickLine={false}
                axisLine={false}
                tick={false}
                width={0}
                domain={[0, 100]}
              />
            )}
            <ChartTooltip
              cursor={{
                stroke: 'hsl(var(--muted-foreground))',
                strokeWidth: 1,
                strokeDasharray: '4 4',
              }}
              content={
                <ChartTooltipContent
                  labelFormatter={(_, payload) => {
                    const punto = (payload?.[0]?.payload ?? {}) as Record<string, unknown>
                    const f = punto.fecha
                    if (!f) return ''
                    const d = parseDateSafe(String(f))
                    const dateStr = esSemanal
                      ? `Semana ${rangoSemana(String(f))}`
                      : /^\d{4}-\d{2}$/.test(String(f))
                        ? d.toLocaleDateString('es-EC', { month: 'long', year: 'numeric' })
                        : d.toLocaleDateString('es-EC', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                          })
                    const pct = punto.percent as number | undefined
                    const validados = punto.validados as number | undefined
                    return (
                      <span className="flex flex-col gap-0.5">
                        <span className="flex items-center justify-between gap-4">
                          <span>{dateStr}</span>
                          {pct != null && (
                            <span className="font-semibold tabular-nums" style={{ color: stroke }}>
                              {pct.toFixed(1)}%
                            </span>
                          )}
                        </span>
                        {validados != null && (
                          <span className="text-[10px] text-muted-foreground">
                            {validados.toLocaleString()} validados
                          </span>
                        )}
                        {tooltipExtra?.(punto)}
                      </span>
                    )
                  }}
                  formatter={(value, name) => {
                    const n = Number(value)
                    if (name === 'percent') {
                      return (
                        <span className="flex w-full items-center justify-between gap-3">
                          <span className="text-muted-foreground">% participación</span>
                          <span className="font-medium tabular-nums" style={{ color: PERCENT_COLOR }}>
                            {n.toFixed(1)}%
                          </span>
                        </span>
                      )
                    }
                    if (name === 'tertiary') {
                      const txt = tertiaryFormatValue
                        ? tertiaryFormatValue(n)
                        : formatNumero(n, configId ?? 0)
                      return (
                        <span className="flex w-full items-center justify-between gap-3">
                          <span className="text-muted-foreground">{tertiaryLabel}</span>
                          <span className="font-medium tabular-nums" style={{ color: TERTIARY_COLOR }}>{txt}</span>
                        </span>
                      )
                    }
                    const isSec = name === 'secondary'
                    const txt = isSec
                      ? secondaryFormatValue
                        ? secondaryFormatValue(n)
                        : formatNumero(n, configId ?? 0)
                      : formatValue
                        ? formatValue(n)
                        : formatNumero(n, configId ?? 0)
                    const lbl = isSec ? secondaryLabel ?? '' : primaryLabel
                    return (
                      <span className="flex w-full items-center justify-between gap-3">
                        <span className="text-muted-foreground">{lbl}</span>
                        <span className="font-medium tabular-nums text-foreground">{txt}</span>
                      </span>
                    )
                  }}
                />
              }
            />
            {/* Barra verde de % participación — detrás de todo */}
            {showPercentBar && (
              <Bar
                yAxisId="percent"
                dataKey="percent"
                fill={PERCENT_COLOR}
                fillOpacity={0.25}
                stroke={PERCENT_COLOR}
                strokeOpacity={0.6}
                strokeWidth={1}
                radius={[2, 2, 0, 0]}
                maxBarSize={32}
              />
            )}
            {/* Serie terciaria (ej. validados acumulados) */}
            {showTertiary && (
              <Line
                yAxisId="tertiary"
                type="monotone"
                dataKey="tertiary"
                stroke={TERTIARY_COLOR}
                strokeWidth={1.5}
                strokeDasharray="6 3"
                dot={false}
                activeDot={{ r: 4, fill: TERTIARY_COLOR, stroke: 'white', strokeWidth: 1.5 }}
              />
            )}
            {/* Serie secundaria (facturas) primero → se dibuja detrás del
                área principal y sus etiquetas. */}
            {showSecondary && (
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="secondary"
                stroke={SECONDARY_COLOR}
                strokeWidth={1.5}
                strokeDasharray="4 3"
                dot={false}
                activeDot={{ r: 4, fill: SECONDARY_COLOR, stroke: 'white', strokeWidth: 1.5 }}
              />
            )}
            <Area
              yAxisId="left"
              type="monotone"
              dataKey="total"
              stroke={stroke}
              strokeWidth={2}
              fill={`url(#area-${colorNombre}-${colorTono})`}
              dot={(props) => {
                const { cx, cy, payload } = props
                if (payload.total > 0) {
                  return (
                    <circle cx={cx} cy={cy} r={3} fill={stroke} stroke="white" strokeWidth={1.5} />
                  )
                }
                return <g />
              }}
              activeDot={{ r: 5, fill: stroke, stroke: 'white', strokeWidth: 2 }}
            >
              <LabelList
                dataKey="total"
                position="top"
                offset={8}
                style={{ fontSize: 10, fontWeight: 500 }}
                formatter={(value: number) =>
                  value > 0 ? (formatValue ? formatValue(value) : value) : ''
                }
                className="fill-foreground"
              />
            </Area>
          </ComposedChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}
